import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useDebugger } from '../contexts/DebuggerContext';
import { v4 as uuidv4 } from 'uuid';
import { useWebSocketChunking } from './useWebSocketChunking';
import { needsChunking } from '../utils/chunking';

/**
 * WebSocket message structure for all server responses.
 *
 * The server uses a consistent message format for all WebSocket communications:
 * - 'response': Progress updates or intermediate responses (e.g., processing status)
 * - 'complete': Final successful response with the result data
 * - 'error': Operation failed with error details
 *
 * @example
 * // Progress update
 * { type: 'response', requestId: '123', data: { status: 'processing', progress: 50 } }
 *
 * // Success response
 * { type: 'complete', requestId: '123', data: { listingKey: '456' } }
 *
 * // Error response
 * { type: 'error', requestId: '123', error: { code: '400', message: 'Invalid input' } }
 */
export interface WebSocketMessage {
  /** Message type indicating the stage of request processing */
  type: 'response' | 'error' | 'complete';
  /** Unique identifier correlating this response to the original request */
  requestId: string;
  /** ISO timestamp when the message was created on the server */
  timestamp: string;
  /** Response payload - structure depends on the operation */
  data?: any;
  /** Error details when type is 'error' */
  error?: {
    /** Error code (e.g., '400', '500', 'VALIDATION_ERROR') */
    code: string;
    /** Human-readable error message */
    message: string;
    /** Additional error context (e.g., validation errors, stack traces in dev) */
    details?: any;
  };
}

/**
 * WebSocket request structure for client-to-server messages.
 *
 * All requests follow this format to enable request-response correlation
 * and proper routing on the backend WebSocket handler.
 *
 * @example
 * {
 *   action: 'LISTING.SAVE',
 *   requestId: 'uuid-v4-here',
 *   payload: { listingKey: '123', listing: { ... } }
 * }
 */
export interface WebSocketRequest {
  /** Action identifier that maps to backend WebSocket route handlers */
  action: string;
  /** Unique request ID for correlating responses - use uuidv4() */
  requestId: string;
  /** Request payload specific to the action */
  payload: any;
}

/**
 * Handlers for progressive/streaming WebSocket responses.
 *
 * Use this for operations that send multiple responses before completion
 * (e.g., listing search with summary + detail responses).
 */
export interface ProgressHandlers {
  /**
   * Called for each 'response' type message.
   * The handler should inspect `data` to determine the response type.
   */
  onProgress?: (data: any) => void;
  /**
   * Called when a 'complete' type message is received.
   */
  onComplete?: (data?: any) => void;
  /**
   * Called when an 'error' type message is received.
   */
  onError?: (error: Error) => void;
}

/**
 * Return type for the useWebSocket hook providing WebSocket operations.
 */
interface UseWebSocketReturn {
  /** Whether the WebSocket is currently connected and ready */
  isConnected: boolean;
  /** Whether a connection attempt is in progress */
  isConnecting: boolean;
  /** Last connection error, if any */
  error: Error | null;
  /**
   * Send a raw WebSocket request with manual request ID management.
   *
   * Use this for advanced scenarios where you need full control over the request.
   * For most cases, use `sendMessageAndWait` instead.
   *
   * @param request - Complete request object with action, requestId, and payload
   * @param timeoutMs - Optional timeout in milliseconds (default: 15 minutes)
   * @returns Promise resolving to the complete WebSocket message
   */
  sendMessage: (
    request: WebSocketRequest,
    timeoutMs?: number,
  ) => Promise<WebSocketMessage>;
  /**
   * Send a message and wait for the response data (convenience method).
   *
   * This method:
   * - Auto-generates a unique requestId
   * - Handles the request-response correlation
   * - Extracts just the data from the response
   * - Properly types the response data with generics
   *
   * @param action - WebSocket action (e.g., 'LISTING.SAVE', 'LISTING.PUBLISH')
   * @param payload - Request payload
   * @param timeoutMs - Optional timeout in milliseconds (default: 15 minutes)
   * @returns Promise resolving to just the response data
   * @throws Error if no data in response or on timeout/connection errors
   *
   * @example
   * const listing = await sendMessageAndWait<SaveListingResponse>(
   *   'LISTING.SAVE',
   *   { listingKey: '123', listing: data },
   *   30000 // 30 second timeout
   * );
   */
  sendMessageAndWait: <T = any>(
    action: string,
    payload: any,
    timeoutMs?: number,
  ) => Promise<T>;
  /**
   * Send a message with progress handlers for streaming/progressive responses.
   *
   * Use this for operations that send multiple responses before completion:
   * - 'response' type messages → calls `onProgress`
   * - 'complete' type message → calls `onComplete` and resolves promise
   * - 'error' type message → calls `onError` and rejects promise
   *
   * This is ideal for listing searches, file uploads, batch operations, etc.
   *
   * @param request - Complete request object with action, requestId, and payload
   * @param handlers - Callbacks for different response types
   * @param timeoutMs - Optional timeout in milliseconds (default: 15 minutes)
   * @returns Promise that resolves when 'complete' is received or rejects on error/timeout
   *
   * @example
   * await sendMessageWithProgressHandlers(
   *   { action: 'LISTING.SEARCH', requestId: uuidv4(), payload: {...} },
   *   {
   *     onProgress: (data) => {
   *       if (data.status === 'listing_summary') {
   *         // Handle summary
   *       } else if (data.status === 'listing_detail') {
   *         // Handle detail
   *       }
   *     },
   *     onComplete: () => console.log('All data received'),
   *     onError: (err) => console.error('Search failed', err)
   *   }
   * );
   */
  sendMessageWithProgressHandlers: (
    request: WebSocketRequest,
    handlers: ProgressHandlers,
    timeoutMs?: number,
  ) => Promise<void>;
  /** Manually close the WebSocket connection */
  disconnect: () => void;
  /** Manually trigger a reconnection attempt */
  reconnect: () => void;
}

/** Default timeout for WebSocket operations - 15 minutes for long-running operations */
const DEFAULT_TIMEOUT_MS = 15 * 60 * 1000;

/**
 * React hook for managing WebSocket connections with automatic reconnection,
 * request-response correlation, and progress updates.
 *
 * Features:
 * - Automatic connection management based on session availability
 * - Request-response correlation using unique request IDs
 * - Progress update support (multiple 'response' messages before 'complete')
 * - Automatic reconnection with exponential backoff
 * - Timeout handling for long-running operations
 * - Type-safe message handling with TypeScript generics
 *
 * Architecture notes:
 * - Uses a Map to track pending requests and their callbacks
 * - Each request has its own timeout to handle long-running operations
 * - Supports streaming responses (multiple 'response' messages)
 * - Clean disconnection prevents reconnection attempts
 *
 * @param enabled - Whether to establish WebSocket connection (useful for conditional connections)
 * @returns Object with WebSocket state and operations
 *
 * @example
 * ```typescript
 * const { sendMessageAndWait, isConnected } = useWebSocket(true);
 *
 * try {
 *   const result = await sendMessageAndWait('LISTING.SAVE', {
 *     listingKey: '123',
 *     listing: listingData
 *   });
 * } catch (error) {
 *   console.error('Save failed:', error);
 * }
 * ```
 */
export const useWebSocket = (enabled: boolean): UseWebSocketReturn => {
  const { session } = useAuth();
  const { wsApi } = useTenant();
  const debug = useDebugger();

  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /** Reference to the active WebSocket instance */
  const wsRef = useRef<WebSocket | null>(null);

  /**
   * Send a raw message without chunking
   */
  const sendRawMessage = useCallback((message: any): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket is not connected'));
        return;
      }

      try {
        wsRef.current.send(JSON.stringify(message));
        resolve();
      } catch (err) {
        reject(err);
      }
    });
  }, []);

  const { sendChunkedMessage } = useWebSocketChunking(sendRawMessage);

  /**
   * Map of pending requests awaiting responses.
   * Key: requestId
   * Value: Promise callbacks, timeout handle, and optional progress handlers
   *
   * This enables request-response correlation in an async environment
   * where multiple requests might be in-flight simultaneously.
   *
   * Supports two patterns:
   * 1. Simple request-response: only resolve/reject are used
   * 2. Progressive responses: handlers are called for intermediate messages
   */
  const requestsRef = useRef<
    Map<
      string,
      {
        resolve: (value: WebSocketMessage) => void;
        reject: (error: Error) => void;
        timeout: NodeJS.Timeout;
        handlers?: ProgressHandlers;
      }
    >
  >(new Map());

  /** Timeout handle for reconnection attempts */
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  /** Current reconnection attempt count for exponential backoff */
  const reconnectAttemptsRef = useRef(0);

  /**
   * Establishes WebSocket connection with session-based authentication.
   *
   * Connection flow:
   * 1. Validates prerequisites (enabled, session, not already connecting)
   * 2. Creates WebSocket with session ID in query params
   * 3. Sets up event handlers for lifecycle events
   * 4. Implements automatic reconnection on unexpected disconnection
   *
   * Reconnection strategy:
   * - Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s (max)
   * - Maximum 5 reconnection attempts
   * - Clean disconnection (code 1000) prevents reconnection
   */
  const connect = useCallback(() => {
    // Guard against multiple connection attempts
    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      isConnecting ||
      !enabled
    ) {
      return;
    }

    const sessionId = session?.id;
    if (!sessionId) {
      const error = new Error('No session found');
      setError(error);
      debug.log('WebSocket', 'No session found for connection', 'error');
      return;
    }

    setIsConnecting(true);
    setError(null);
    try {
      // Session ID is passed as query param for API Gateway authorization
      const ws = new WebSocket(`${wsApi}?session=${sessionId}`);

      /**
       * Connection established - WebSocket is ready for messages
       */
      ws.onopen = () => {
        debug.log('WebSocket', 'Connection established', 'info');
        setIsConnected(true);
        setIsConnecting(false);
        setError(null);
        reconnectAttemptsRef.current = 0; // Reset reconnection counter
      };

      /**
       * Connection closed - handles both clean and unexpected disconnections
       */
      ws.onclose = (event) => {
        debug.log(
          'WebSocket',
          `Connection closed - Code: ${event.code}, Reason: ${event.reason}`,
          'warn',
        );
        setIsConnected(false);
        setIsConnecting(false);

        // Reject all pending requests since connection is lost
        requestsRef.current.forEach(({ reject, timeout }) => {
          clearTimeout(timeout);
          reject(new Error('WebSocket connection closed'));
        });
        requestsRef.current.clear();

        // Implement reconnection logic for unexpected disconnections
        // Code 1000 = Normal Closure (user initiated or expected)
        if (event.code !== 1000 && reconnectAttemptsRef.current < 5) {
          // Exponential backoff: 1s, 2s, 4s, 8s, 16s, capped at 30s
          const delay = Math.min(
            1000 * Math.pow(2, reconnectAttemptsRef.current),
            30000,
          );
          debug.log(
            'WebSocket',
            `Scheduling reconnect attempt ${reconnectAttemptsRef.current + 1} in ${delay}ms`,
            'info',
          );
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connect();
          }, delay);
        }
      };

      /**
       * Connection error - typically network issues or invalid endpoint
       */
      ws.onerror = (event) => {
        debug.log('WebSocket', 'Connection error occurred', 'error');
        setError(new Error('WebSocket connection error'));
      };

      /**
       * Message received from server - handles all message types.
       *
       * Message flow:
       * 1. Parse JSON message
       * 2. Find corresponding request by requestId
       * 3. Route to appropriate handler based on message type:
       *    - 'response': Call onProgress handler (if provided), keep request alive
       *    - 'complete': Call onComplete handler, resolve promise, clean up
       *    - 'error': Call onError handler, reject promise, clean up
       *
       * This supports both:
       * - Simple request-response (sendMessage/sendMessageAndWait)
       * - Progressive/streaming responses (sendMessageWithProgressHandlers)
       */
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WebSocketMessage;
          const request = requestsRef.current.get(message.requestId);

          if (request) {
            if (message.type === 'error') {
              // Error message - terminal, clean up
              clearTimeout(request.timeout);
              requestsRef.current.delete(message.requestId);

              debug.log(
                'WebSocket',
                `Request ${message.requestId} failed: ${message.error?.message}${message.error?.details && message.error.details.length ? '--' + JSON.stringify(message.error.details, null, 2) : ''}`,
                'error',
              );

              // Enhance error with additional context from server
              const error = new Error(
                message.error?.message || 'Operation failed',
              );
              (error as any).code = message.error?.code;
              (error as any).details = message.error?.details;

              // Call error handler if provided
              if (request.handlers?.onError) {
                request.handlers.onError(error);
              }

              request.reject(error);
            } else if (message.type === 'complete') {
              // Complete message - terminal, clean up
              clearTimeout(request.timeout);
              requestsRef.current.delete(message.requestId);

              // Call complete handler if provided
              if (request.handlers?.onComplete) {
                request.handlers.onComplete(message.data);
              }

              request.resolve(message);
            } else if (message.type === 'response') {
              // Progress/intermediate message - keep request alive
              if (request.handlers?.onProgress) {
                request.handlers.onProgress(message.data);
              }
              // Don't delete request - more responses may be coming
            }
          }
        } catch (err) {
          debug.log('WebSocket', `Failed to parse message: ${err}`, 'error');
        }
      };

      wsRef.current = ws;
    } catch (err) {
      debug.log('WebSocket', `Failed to create connection: ${err}`, 'error');
      setError(err as Error);
      setIsConnecting(false);
    }
  }, [wsApi, session?.id, debug, enabled]);

  /**
   * Gracefully disconnect WebSocket and prevent reconnection attempts.
   * Use this for user-initiated disconnections or component cleanup.
   */
  const disconnect = useCallback(() => {
    // Clear any pending reconnection
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    if (wsRef.current) {
      // Code 1000 = Normal Closure (prevents auto-reconnection)
      wsRef.current.close(1000, 'User initiated disconnect');
      wsRef.current = null;
    }
  }, [debug]);

  /**
   * Send a WebSocket message with timeout and response correlation.
   *
   * This is the low-level send method that:
   * 1. Validates connection state
   * 2. Sets up timeout handling
   * 3. Stores promise callbacks for response correlation
   * 4. Sends the JSON message
   *
   * The promise resolves when a 'complete' message is received
   * or rejects on 'error', timeout, or connection loss.
   *
   * @internal Most users should use sendMessageAndWait instead
   */
  const sendMessage = useCallback(
    (
      request: WebSocketRequest,
      timeoutMs: number = DEFAULT_TIMEOUT_MS,
    ): Promise<WebSocketMessage> => {
      return new Promise(async (resolve, reject) => {
        // Validate connection state
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          const error = new Error('WebSocket is not connected');
          debug.log(
            'WebSocket',
            'Cannot send message - not connected',
            'error',
          );
          reject(error);
          return;
        }

        const messageStr = JSON.stringify(request);
        const messageSize = new Blob([messageStr]).size;

        // Set up timeout to prevent hanging requests
        const timeout = setTimeout(() => {
          requestsRef.current.delete(request.requestId);
          reject(new Error(`Request timeout after ${timeoutMs}ms`));
        }, timeoutMs);

        // Store callbacks for response correlation
        requestsRef.current.set(request.requestId, {
          resolve,
          reject,
          timeout,
        });

        try {
          // Check if message needs chunking
          if (needsChunking(messageStr)) {
            // Use chunked message sending
            await sendChunkedMessage(request, {});
          } else {
            // Send normally for small messages
            await sendRawMessage(request);
          }
        } catch (err) {
          // Clean up on send failure
          clearTimeout(timeout);
          requestsRef.current.delete(request.requestId);
          debug.log('WebSocket', `Failed to send message: ${err}`, 'error');
          reject(err);
        }
      });
    },
    [debug, sendRawMessage, sendChunkedMessage],
  );

  /**
   * High-level method for sending messages with automatic request ID generation.
   *
   * This is the recommended method for most WebSocket operations as it:
   * - Generates unique request IDs automatically
   * - Returns just the response data (not the envelope)
   * - Provides better TypeScript inference with generics
   * - Simplifies error handling
   *
   * @see sendMessage for the underlying implementation
   */
  const sendMessageAndWait = useCallback(
    async <T = any>(
      action: string,
      payload: any,
      timeoutMs: number = DEFAULT_TIMEOUT_MS,
    ): Promise<T> => {
      const request: WebSocketRequest = {
        action,
        requestId: uuidv4(), // Generate unique ID for correlation
        payload,
      };

      const response = await sendMessage(request, timeoutMs);

      // Extract data from successful response
      if (response.type === 'complete' && response.data !== undefined) {
        return response.data as T;
      }

      // This shouldn't happen with well-behaved backend
      throw new Error('No data received in response');
    },
    [sendMessage, debug],
  );

  /**
   * Send a message with progress handlers for streaming/progressive responses.
   *
   * This method enables handling of operations that send multiple responses:
   * 1. 'response' type messages trigger the onProgress handler
   * 2. 'complete' type message triggers onComplete and resolves the promise
   * 3. 'error' type message triggers onError and rejects the promise
   *
   * Use this for:
   * - Listing searches (summary + details)
   * - File uploads with progress
   * - Batch operations
   * - Any operation that streams data
   *
   * @param request - Complete request object with action, requestId, and payload
   * @param handlers - Callbacks for different response types
   * @param timeoutMs - Optional timeout in milliseconds (default: 15 minutes)
   * @returns Promise that resolves when 'complete' is received
   */
  const sendMessageWithProgressHandlers = useCallback(
    (
      request: WebSocketRequest,
      handlers: ProgressHandlers,
      timeoutMs: number = DEFAULT_TIMEOUT_MS,
    ): Promise<void> => {
      return new Promise((resolve, reject) => {
        // Validate connection state
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          const error = new Error('WebSocket is not connected');
          debug.log(
            'WebSocket',
            'Cannot send message - not connected',
            'error',
          );
          if (handlers.onError) {
            handlers.onError(error);
          }
          reject(error);
          return;
        }

        const messageStr = JSON.stringify(request);

        // Set up timeout to prevent hanging requests
        const timeout = setTimeout(() => {
          requestsRef.current.delete(request.requestId);
          const error = new Error(`Request timeout after ${timeoutMs}ms`);
          if (handlers.onError) {
            handlers.onError(error);
          }
          reject(error);
        }, timeoutMs);

        // Store callbacks and handlers for response correlation
        requestsRef.current.set(request.requestId, {
          resolve: (_message: WebSocketMessage) => {
            // Resolve with void (handlers already called)
            resolve();
          },
          reject,
          timeout,
          handlers, // Store handlers for onmessage to use
        });

        // Send the message (async operation handled without async executor)
        const sendAsync = async () => {
          try {
            // Check if message needs chunking
            if (needsChunking(messageStr)) {
              // Use chunked message sending
              await sendChunkedMessage(request, {});
            } else {
              // Send normally for small messages
              await sendRawMessage(request);
            }
          } catch (err) {
            // Clean up on send failure
            clearTimeout(timeout);
            requestsRef.current.delete(request.requestId);
            debug.log('WebSocket', `Failed to send message: ${err}`, 'error');
            const error =
              err instanceof Error ? err : new Error('Failed to send message');
            if (handlers.onError) {
              handlers.onError(error);
            }
            reject(error);
          }
        };

        sendAsync();
      });
    },
    [debug, sendRawMessage, sendChunkedMessage],
  );

  /**
   * Effect to manage WebSocket lifecycle based on session availability.
   *
   * Connection is established when:
   * - Hook is enabled
   * - Session ID is available
   * - WebSocket API URL is configured
   * - No existing connection
   *
   * Cleanup cancels any pending reconnection timers.
   */
  useEffect(() => {
    if (enabled && session?.id && wsApi && !wsRef.current) {
      connect();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [session?.id, wsApi, enabled]); // Only depend on actual values, not functions

  return {
    isConnected,
    isConnecting,
    error,
    sendMessage,
    sendMessageAndWait,
    sendMessageWithProgressHandlers,
    disconnect,
    reconnect: connect,
  };
};
